import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, ClipboardList, Loader2, DoorOpen, Ban, UserCog, Search, ArrowRightLeft, Receipt } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge } from "@/components/Ui";
import ManifestDialog from "@/pages/admin/ManifestDialog";
import { SettlementPanel } from "@/pages/admin/SettlementPanel";
import SearchSelect from "@/components/SearchSelect";
import { useAuth, userRoles } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money, fmtTime, todayStr, statusStyle } from "@/lib/format";

const nowStr = () => new Date().toISOString().slice(0, 16);
const isOpen = (t) => t.status === "scheduled" || t.status === "boarding";

const Pick = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

function EditTrip({ trip, lists, onClose, onDone }) {
  const [f, setF] = useState({});
  useEffect(() => { if (trip) setF({ driver_id: trip.driver_id, bus_id: trip.bus_id, fare: trip.fare, bus_type_id: trip.bus_type_id, time: fmtTime(trip.departure) }); }, [trip]);
  const save = async () => {
    try { await api.patch(`/admin/trips/${trip.id}`, { ...f, fare: Number(f.fare) }); toast.success("Trip updated"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pickBus = async (v) => {
    setF((p) => ({ ...p, bus_id: v }));
    if (!v) return;
    const { data } = await api.get(`/admin/buses/${v}/last-driver`);
    if (data.driver_id) { setF((p) => ({ ...p, driver_id: data.driver_id })); toast.message(`Driver filled from this bus's last trip: ${data.driver_name}`); }
  };
  const type = lists.bus_types.find((t) => t.id === f.bus_type_id);
  const typeCap = type?.capacity;
  const buses = lists.buses.filter((b) => b.active !== false && (!typeCap || b.capacity === typeCap));
  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="trip-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit trip</DialogTitle></DialogHeader>
        {trip && <p className="text-sm text-slate-500">{trip.route_name || `${trip.origin_city} → ${trip.destination_city}`} · {trip.date}</p>}
        <div className="grid grid-cols-2 gap-3">
          <Pick label="Bus type" value={f.bus_type_id} onChange={(v) => setF({ ...f, bus_type_id: v, bus_id: lists.buses.some((b) => b.id === f.bus_id && b.bus_type_id === v) ? f.bus_id : null })} testid="edit-trip-bus-type-select"
            options={lists.bus_types.filter((t) => t.active !== false).map((t) => ({ value: t.id, label: `${t.name} · ${t.capacity}` }))} />
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Departure time</Label><Input type="time" value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="edit-trip-time-input" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Bus number</Label>
          <SearchSelect value={f.bus_id} onChange={pickBus} testid="edit-trip-bus-select" placeholder="Type registration or fleet name"
            options={buses.map((b) => ({ value: b.id, label: b.number, sub: `${b.name ? `${b.name} · ` : ""}${b.bus_type} · ${b.capacity} seats${b.owner_name ? ` · ${b.owner_name}` : ""}` }))} /></div>
        {!buses.length && <p className="text-xs text-amber-600" data-testid="no-bus-of-type">No active {typeCap}-seat bus is available - add one under Buses.</p>}
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Driver (filled from the bus's last trip)</Label>
          <SearchSelect value={f.driver_id} onChange={(v) => setF({ ...f, driver_id: v })} testid="edit-trip-driver-select" placeholder="Type driver name, phone or licence"
            options={lists.drivers.filter((d) => d.active).map((d) => ({ value: d.id, label: d.name, sub: [d.phone, d.license_no].filter(Boolean).join(" · ") }))} /></div>
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Fare</Label><Input type="number" value={f.fare || ""} onChange={(e) => setF({ ...f, fare: e.target.value })} data-testid="edit-trip-fare-input" /></div>
        <Button onClick={save} className="rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="edit-trip-save-button">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function AddTrip({ base, lists, onClose, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (base) setF({ route_id: base.route_id, bus_type_id: base.bus_type_id || "", date: base.date, time: "", bus_id: null, driver_id: null }); }, [base]);
  const route = lists.routes.find((r) => r.id === f.route_id);
  const type = lists.bus_types.find((t) => t.id === f.bus_type_id);
  const typeCap = type?.capacity;
  const buses = lists.buses.filter((b) => b.active !== false && (!typeCap || b.capacity === typeCap));
  const pickBus = async (v) => {
    setF((p) => ({ ...p, bus_id: v }));
    if (!v) return;
    try {
      const { data } = await api.get(`/admin/buses/${v}/last-driver`);
      if (data.driver_id) { setF((p) => ({ ...p, driver_id: data.driver_id })); toast.message(`Driver filled from this bus's last trip: ${data.driver_name}`); }
    } catch { /* ignore */ }
  };
  const save = async () => {
    setBusy(true);
    try { await api.post("/admin/trips", { route_id: f.route_id, date: f.date, time: f.time, bus_type_id: f.bus_type_id, bus_id: f.bus_id || null, driver_id: f.driver_id || null }); toast.success("Trip added"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!base} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="add-trip-dialog">
        <DialogHeader><DialogTitle className="font-display">Add trip</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500" data-testid="add-trip-timetable">Extra trip on <b>{route?.name || base?.route_name || `${base?.origin_city} → ${base?.destination_city}`}</b> · {base?.date}</p>
        <div className="grid grid-cols-2 gap-3">
          <Pick label="Bus type" value={f.bus_type_id} onChange={(v) => setF({ ...f, bus_type_id: v, bus_id: null })} testid="add-trip-bus-type-select"
            options={lists.bus_types.filter((t) => t.active !== false).map((t) => ({ value: t.id, label: `${t.name} · ${t.capacity} seats` }))} />
          <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Departure time</Label><Input type="time" value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="add-trip-time-input" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Bus number (optional — assign now or later)</Label>
          <SearchSelect value={f.bus_id} onChange={pickBus} testid="add-trip-bus-select" placeholder="Type registration or fleet name"
            options={buses.map((b) => ({ value: b.id, label: b.number, sub: `${b.name ? `${b.name} · ` : ""}${b.bus_type} · ${b.capacity} seats${b.owner_name ? ` · ${b.owner_name}` : ""}` }))} /></div>
        {f.bus_type_id && !buses.length && <p className="text-xs text-amber-600" data-testid="add-trip-no-bus">No active {typeCap}-seat bus is available - assign one later or add it under Buses.</p>}
        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Driver (optional)</Label>
          <SearchSelect value={f.driver_id} onChange={(v) => setF({ ...f, driver_id: v })} testid="add-trip-driver-select" placeholder="Type driver name, phone or licence"
            options={lists.drivers.filter((d) => d.active).map((d) => ({ value: d.id, label: d.name, sub: [d.phone, d.license_no].filter(Boolean).join(" · ") }))} /></div>
        <Button disabled={busy || !f.bus_type_id || !f.time} onClick={save} className="h-11 rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="add-trip-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add trip"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CancelTrip({ trip, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [trip]);
  const save = async () => {
    try { await api.patch(`/admin/trips/${trip.id}`, { status: "cancelled", cancel_reason: reason }); toast.success("Trip cancelled"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="cancel-trip-dialog">
        <DialogHeader><DialogTitle className="font-display">Cancel trip</DialogTitle></DialogHeader>
        {trip && <p className="text-sm text-slate-500">{trip.origin_city} → {trip.destination_city} · {trip.date} {fmtTime(trip.departure)} · {trip.booked} booked</p>}
        {trip?.booked > 0 && <p className="rounded-xl bg-rose-50 p-3 text-xs font-semibold text-rose-700" data-testid="cancel-trip-booked-warning">Passengers have booked tickets on this trip. Transfer or cancel their tickets first.</p>}
        <Label className="text-xs font-semibold text-slate-600">Reason for cancelling</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Bus broke down, low demand" data-testid="cancel-trip-reason-input" />
        <Button disabled={reason.trim().length < 3} onClick={save} className="rounded-xl bg-rose-600 font-bold hover:bg-rose-700" data-testid="cancel-trip-confirm-button">Cancel trip</Button>
      </DialogContent>
    </Dialog>
  );
}

function TripTransferDialog({ trip, onClose, onDone }) {
  const [groups, setGroups] = useState(null);
  const [edits, setEdits] = useState({});
  const [date, setDate] = useState("");
  const [destTrips, setDestTrips] = useState([]);
  const [dest, setDest] = useState(null);
  const [destPax, setDestPax] = useState([]);
  const [busy, setBusy] = useState("");
  const loadSource = useCallback(() => {
    if (!trip) return;
    api.get(`/trips/${trip.id}/manifest`).then((r) => {
      const map = {};
      r.data.passengers.forEach((p) => { if (!map[p.code]) map[p.code] = { code: p.code, status: p.status, from_city: p.from_city, to_city: p.to_city, passengers: [] }; map[p.code].passengers.push(p); });
      const gs = Object.values(map);
      setGroups(gs);
      setEdits(Object.fromEntries(gs.map((g) => [g.code, g.passengers.map((p) => String(p.seat))])));
    }).catch((e) => { toast.error(errMsg(e)); setGroups([]); });
  }, [trip]);
  useEffect(() => { setGroups(null); setDest(null); setDestPax([]); setDestTrips([]); setDate(trip ? trip.date : ""); loadSource(); }, [trip, loadSource]);
  useEffect(() => {
    if (!trip || !date) return;
    setDest(null); setDestPax([]);
    api.get(`/trips/${trip.id}/transfer-trips`, { params: { date } }).then((r) => setDestTrips(r.data)).catch((e) => { setDestTrips([]); toast.error(errMsg(e)); });
  }, [trip, date]);
  const pickDest = async (t) => {
    setDest(t);
    try { const { data } = await api.get(`/trips/${t.id}/manifest`); setDestPax(data.passengers); } catch { setDestPax([]); }
  };
  const setSeat = (code, i, v) => setEdits((e) => ({ ...e, [code]: e[code].map((s, j) => (j === i ? v.replace(/\D/g, "").slice(0, 3) : s)) }));
  const move = async (g) => {
    if (!dest) return toast.error("Pick a destination trip first");
    const seats = (edits[g.code] || []).map((s) => Number(s));
    if (seats.some((s) => !s)) return toast.error("Enter a seat number for each passenger");
    setBusy(g.code);
    try {
      await api.post(`/tickets/${g.code}/transfer`, { trip_id: dest.id, seats });
      toast.success(`${g.code} moved to ${fmtTime(dest.departure)}${dest.same ? " (reseated)" : ""}`);
      await loadSource(); await pickDest(dest); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const takenSeats = destPax.map((p) => p.seat).sort((a, b) => a - b);
  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-5xl" data-testid="trip-transfer-dialog">
        <DialogHeader><DialogTitle className="font-display">Transfer passengers / change seats</DialogTitle></DialogHeader>
        {trip && <p className="text-sm text-slate-500">{trip.origin_city} → {trip.destination_city} · {trip.date} {fmtTime(trip.departure)}</p>}
        {groups === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0052FF]" /> : (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2" data-testid="xfer-source-groups">
              <div className="label-xs">Passengers on this trip · edit the seat, pick a destination, then Move</div>
              {groups.map((g) => {
                const movable = ["confirmed", "reserved"].includes(g.status);
                return (
                  <div key={g.code} className="card-soft space-y-2 p-3" data-testid={`xfer-group-${g.code}`}>
                    <div className="flex flex-wrap items-center gap-2"><span className="font-mono font-bold">{g.code}</span><StatusBadge status={g.status} cls={statusStyle[g.status]} /><span className="text-xs text-slate-500">{g.from_city} → {g.to_city}</span></div>
                    {g.passengers.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="min-w-0 flex-1 truncate">{p.name}</span>
                        <span className="text-xs text-slate-400">seat</span>
                        <Input value={edits[g.code]?.[i] ?? ""} onChange={(e) => setSeat(g.code, i, e.target.value)} className="h-9 w-16 text-center" inputMode="numeric" data-testid={`xfer-seat-${g.code}-${i}`} />
                      </div>
                    ))}
                    <Button size="sm" disabled={!dest || !movable || busy === g.code} onClick={() => move(g)} className="rounded-full bg-[#0052FF] hover:bg-[#0041CC]" data-testid={`xfer-move-${g.code}`}>
                      {busy === g.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRightLeft className="h-4 w-4" />Move</>}
                    </Button>
                    {!movable && <p className="text-xs text-slate-400">A {g.status} ticket cannot be moved.</p>}
                  </div>
                );
              })}
              {!groups.length && <p className="card-soft p-4 text-center text-sm text-slate-500">No passengers on this trip.</p>}
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2"><Label className="text-xs font-semibold text-slate-600">Transfer to trips on</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="xfer-dest-date" /></div>
              <div className="max-h-56 space-y-2 overflow-y-auto" data-testid="xfer-dest-trips">
                {destTrips.map((t) => (
                  <button key={t.id} type="button" onClick={() => pickDest(t)} data-testid={`xfer-dest-trip-${t.id}`}
                    className={`card-soft btn-press flex w-full items-center gap-3 p-3 text-left ${dest?.id === t.id ? "ring-2 ring-[#0052FF]" : ""}`}>
                    <span className="font-display text-lg font-extrabold">{fmtTime(t.departure)}</span>
                    <span className="min-w-0 flex-1 text-sm"><b className="block truncate">{t.origin_city} → {t.destination_city}{t.same ? " · same trip" : ""}</b><span className="text-xs text-slate-500">{t.bus_type}{t.bus_number ? ` · Bus ${t.bus_number}` : ""} · cap {t.capacity}</span></span>
                    <span className="text-xs font-semibold text-emerald-700">{t.seats_available} free</span>
                  </button>
                ))}
                {!destTrips.length && <p className="card-soft p-4 text-center text-sm text-slate-500">No suitable trips on this date.</p>}
              </div>
              {dest && (
                <div className="card-soft p-3" data-testid="xfer-dest-pax">
                  <div className="label-xs mb-1">On {fmtTime(dest.departure)} · taken seats</div>
                  <div className="text-sm font-semibold">{takenSeats.length ? takenSeats.join(", ") : "No seats taken yet"}</div>
                  <div className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs text-slate-500">
                    {destPax.map((p) => <div key={`${p.code}-${p.seat}`}>Seat {p.seat} · {p.name} ({p.code})</div>)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TripRow({ t, onManifest, onAssign, onStatus, onCancel, onAdd, onTransfer, onSignout }) {
  return (
    <div className="card-soft flex flex-wrap items-center gap-3 p-4" data-testid={`admin-trip-row-${t.id}`}>
      <div className="w-20 font-display text-2xl font-extrabold">{fmtTime(t.departure)}</div>
      <div className="min-w-[240px] flex-1 basis-[320px]">
        <div className="font-display font-bold">{t.route_name ? `${t.route_name} · ` : ""}{t.origin_city} → {t.destination_city} <span className="text-sm font-semibold text-[#0052FF]">{money(t.fare)}</span>{t.extra && <span className="chip ml-2 bg-violet-50 text-violet-700" data-testid={`trip-extra-${t.id}`}>Extra</span>}</div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
          <span>{t.bus_type}</span>
          {t.bus_assigned ? <span>· Bus {t.bus_number}</span> : <span className="chip bg-amber-50 text-amber-700" data-testid={`trip-no-bus-${t.id}`}>Bus not assigned</span>}
          {t.driver_assigned ? <span>· Driver {t.driver_name}</span> : <span className="chip bg-amber-50 text-amber-700" data-testid={`trip-no-driver-${t.id}`}>Driver not assigned</span>}
        </div>
        {t.status === "cancelled" && t.cancel_reason && <div className="mt-1 text-xs text-rose-600" data-testid={`trip-cancel-info-${t.id}`}>Cancelled by {t.cancelled_by} on {new Date(t.cancelled_at).toLocaleString("en-GB")}: {t.cancel_reason}</div>}
      </div>
      <div className="w-full lg:w-44" data-testid={`trip-capacity-${t.id}`}>
        <div className="mb-1 text-xs font-semibold text-slate-600">Capacity {t.capacity} · Booked {t.booked}</div>
        <Progress value={(t.booked / (t.capacity || 1)) * 100} className="h-2" />
      </div>
      <StatusBadge status={t.status} cls={statusStyle[t.status]} />
      {isOpen(t) && t.departure < nowStr() && <span className="chip bg-rose-100 text-rose-700" data-testid={`signout-due-${t.id}`}>Sign-out due</span>}
      <div className="flex w-full flex-wrap justify-end gap-1.5">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onManifest(t)} data-testid={`trip-manifest-button-${t.id}`}><ClipboardList className="h-4 w-4" />{isOpen(t) ? "Print manifest" : "Manifest & account"}</Button>
        {t.status === "departed" && <Button size="sm" variant="outline" className="rounded-full border-emerald-200 text-emerald-700" onClick={() => onSignout(t)} data-testid={`trip-reprint-signout-${t.id}`}><Receipt className="h-4 w-4" />Reprint sign-out account</Button>}
        {isOpen(t) && (
          <>
            {t.status === "scheduled" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onStatus(t, "boarding")} data-testid={`trip-boarding-button-${t.id}`}><DoorOpen className="h-4 w-4" />Open boarding</Button>}
            <Button size="sm" variant={t.bus_assigned && t.driver_assigned ? "outline" : "default"} className={`rounded-full ${t.bus_assigned && t.driver_assigned ? "" : "bg-amber-500 hover:bg-amber-600"}`} onClick={() => onAssign(t)} data-testid={`trip-edit-button-${t.id}`}><UserCog className="h-4 w-4" />{t.bus_assigned && t.driver_assigned ? "Edit bus & driver" : "Assign bus & driver"}</Button>
            {t.booked > 0 && <Button size="sm" variant="outline" className="rounded-full border-sky-200 text-sky-700" onClick={() => onTransfer(t)} data-testid={`trip-transfer-button-${t.id}`}><ArrowRightLeft className="h-4 w-4" />Transfer passengers</Button>}
            <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-600" onClick={() => onCancel(t)} data-testid={`trip-cancel-button-${t.id}`}><Ban className="h-4 w-4" />Cancel trip</Button>
          </>
        )}
        <Button size="sm" variant="outline" className="rounded-full border-violet-200 text-violet-700" onClick={() => onAdd(t)} data-testid={`trip-add-button-${t.id}`}><Plus className="h-4 w-4" />Add trip</Button>
      </div>
    </div>
  );
}

function SignoutDialog({ trip, onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    setS(null);
    if (trip) api.get(`/trips/${trip.id}/manifest`).then((r) => setS(r.data.settlement)).catch((e) => toast.error(errMsg(e)));
  }, [trip]);
  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-4xl" data-testid="signout-reprint-dialog">
        <DialogHeader><DialogTitle className="font-display">Sign-out account — reprint</DialogTitle></DialogHeader>
        {!trip ? null : s === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0052FF]" />
          : !s ? <p className="p-4 text-center text-sm text-slate-500" data-testid="signout-none">No sign-out account slip found for this bus.</p>
          : <SettlementPanel s={s} readOnly />}
      </DialogContent>
    </Dialog>
  );
}

export default function TripsAdmin({ lists }) {
  const { user } = useAuth();
  const roles = userRoles(user);
  const privileged = roles.includes("admin") || roles.includes("manager");
  const smLocked = (roles.includes("station_master") || roles.includes("teller")) && !privileged && !!user?.station_id;
  const [date, setDate] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState("pending");
  const [station, setStation] = useState(smLocked ? user.station_id : "all");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [trips, setTrips] = useState(null);
  const [editing, setEditing] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [signout, setSignout] = useState(null);
  const load = useCallback(() => api.get("/admin/trips", { params: { date } }).then((r) => setTrips(r.data)), [date]);
  useEffect(() => { setTrips(null); load(); }, [load]);
  const setStatus = async (t, status) => {
    try { await api.patch(`/admin/trips/${t.id}`, { status }); toast.success(`Departure ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const term = q.trim().toLowerCase();
  const matchStatus = (t) => statusFilter === "all" ? true
    : statusFilter === "pending" ? isOpen(t)
    : ["departed", "closed"].includes(t.status);
  const shown = (trips || []).filter((t) => (station === "all" || t.origin_id === station)
    && matchStatus(t)
    && (!term || [t.bus_number, t.bus_name, t.route_name, t.origin_city, t.destination_city, t.driver_name, t.driver_phone].some((v) => (v || "").toLowerCase().includes(term))));
  return (
    <div className="space-y-6" data-testid="trips-admin">
      <div className="space-y-3">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Trip management</h2>
        <div className="card-soft grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-[200px_160px_150px_1fr] lg:items-center" data-testid="trips-filters">
          <Select value={station} onValueChange={setStation} disabled={smLocked}>
            <SelectTrigger data-testid="trips-station-select"><SelectValue /></SelectTrigger>
            <SelectContent>{!smLocked && <SelectItem value="all">All stations</SelectItem>}{lists.stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="trips-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="dispatched">Dispatched</SelectItem>
              <SelectItem value="all">All trips</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="trips-date-input" />
          <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bus number, route, driver name or phone" className="pl-9" data-testid="trips-search-input" /></div>
        </div>
        {trips === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0052FF]" />}
        {trips && shown.length === 0 && <p className="card-soft p-6 text-center text-sm text-slate-500" data-testid="trips-empty">No departures match.</p>}
        <div className="space-y-2">{shown.map((t) => <TripRow key={t.id} t={t} onManifest={setManifest} onAssign={setEditing} onStatus={setStatus} onCancel={setCancelling} onAdd={setAdding} onTransfer={setTransferring} onSignout={setSignout} />)}</div>
      </div>
      <SignoutDialog trip={signout} onClose={() => setSignout(null)} />
      <AddTrip base={adding} lists={lists} onClose={() => setAdding(null)} onDone={() => { setAdding(null); load(); }} />
      <CancelTrip trip={cancelling} onClose={() => setCancelling(null)} onDone={() => { setCancelling(null); load(); }} />
      <EditTrip trip={editing} lists={lists} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      <TripTransferDialog trip={transferring} onClose={() => setTransferring(null)} onDone={load} />
      <ManifestDialog trip={manifest} onClose={() => setManifest(null)} onChanged={load} canRelease={false} />
    </div>
  );
}
